import React, { useState, useEffect } from 'react'
import {
	EuiModal,
	EuiModalHeader,
	EuiModalHeaderTitle,
	EuiModalBody,
	EuiModalFooter,
	EuiButton,
	EuiButtonEmpty,
	EuiFormRow,
	EuiTextArea,
} from '@equipmentshare/ds2'

export type NotesModalProps = {
	/** Whether the modal is open */
	isOpen: boolean,
	/** Callback when modal should close */
	onClose: () => void,
	/** Current notes value */
	notes: string,
	/** Callback when notes are saved */
	onSave: ( notes: string ) => void,
	/** Title for the modal (e.g., "Add Note - Line Item 1" or "Add Note") */
	title?: string,
	/** Placeholder text for the textarea */
	placeholder?: string,
}

/**
 * Reusable modal for adding/editing notes
 * Can be used for both line items and header notes
 */
export default function NotesModal( {
	isOpen,
	onClose,
	notes,
	onSave,
	title,
	placeholder = 'Add notes...',
}: NotesModalProps ) {
	const [ localNotes, setLocalNotes ] = useState( notes )

	// Update local state when notes prop changes
	useEffect( () => {
		setLocalNotes( notes )
	}, [ notes ] )

	const handleNotesChange = ( event: React.ChangeEvent<HTMLTextAreaElement> ) => {
		const value = event.target.value
		// Limit to 1000 characters
		if ( value.length <= 1000 ) {
			setLocalNotes( value )
		}
	}

	const handleSave = () => {
		onSave( localNotes )
		onClose()
	}

	const handleCancel = () => {
		// Reset to original notes on cancel
		setLocalNotes( notes )
		onClose()
	}

	if ( !isOpen ) {
		return null
	}

	const hasNotes = notes.trim().length > 0
	const modalTitle = title ?? ( hasNotes ? 'Edit Note' : 'Add Note' )

	return (
		<EuiModal onClose={handleCancel} style={{ width: 600 }}>
			<EuiModalHeader>
				<EuiModalHeaderTitle>
					{modalTitle}
				</EuiModalHeaderTitle>
			</EuiModalHeader>

			<EuiModalBody>
				<EuiFormRow
					label="Notes"
					helpText={`${localNotes.length}/1000 characters`}
					fullWidth
				>
					<EuiTextArea
						value={localNotes}
						onChange={handleNotesChange}
						placeholder={placeholder}
						rows={6}
						fullWidth
						autoFocus
					/>
				</EuiFormRow>
			</EuiModalBody>

			<EuiModalFooter>
				<EuiButtonEmpty onClick={handleCancel}>
					Cancel
				</EuiButtonEmpty>
				<EuiButton onClick={handleSave} fill>
					Save
				</EuiButton>
			</EuiModalFooter>
		</EuiModal>
	)
}
